import React, { useState, useEffect, useRef } from 'react';
import Input from '../Input';
import AddMeasurementModal from './AddMeasurementModal';
import { isActivityOngoing, isActivityExpired } from './Activity';
import { CheckIcon, TrashIcon } from '@heroicons/react/24/solid';
import { useUnits } from '../../contexts/UnitsContext';
import { formatWeight } from '../../utils/units';
import { IDLE_COMPLETION, SUBMIT_NOUNS, SubmitFeedbackAnnouncement, SubmitFeedbackButton } from './SubmitFeedback';
import './Metrics.css';

const validWeight = row => ['kg', 'lb', 'lbs'].includes(row.unit) && typeof row.value === 'number' && Number.isFinite(row.value) && row.value > 0;

const MetricsEditor = ({ metric, finishActivity, completion = IDLE_COMPLETION, getCompletionContext, backendService, modalService, readOnly = false }) => {
  // When readOnly, treat the activity as non-ongoing so inputs display as static values
  const ongoing = !readOnly && isActivityOngoing(metric);
  const { weightUnit } = useUnits();
  const [measurements, setMeasurements] = useState(metric.data.measurements || []);
  const measurementsRef = useRef(measurements);
  const updateMeasurements = update => {
    const next = update(measurementsRef.current);
    measurementsRef.current = next;
    setMeasurements(next);
  };
  const [availableTypes, setAvailableTypes] = useState([]);
  // Input owns focus, not its value: preserve raw text here (including decimal
  // intermediates) rather than feeding it formatted asynchronous server echoes.
  const [drafts, setDrafts] = useState({});
  // The metric endpoint replaces the entire measurements array. Serialize all
  // row writes/adds/removals, not just writes for the same measurement type.
  const saves = useRef(Promise.resolve());
  const failedSaves = useRef(new Map());
  const failedAdds = useRef(new Set());
  const [saveError, setSaveError] = useState('');
  const mounted = useRef(true);
  const submitting = useRef(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isCompleting, setIsCompleting] = useState(false);
  const completing = useRef(false);
  const latestValues = useRef(new Map());
  const invalidDrafts = useRef(new Set());
  const removing = useRef(new Set());
  const [removingTypes, setRemovingTypes] = useState(new Set());
  const skipBlurChange = useRef(false);
  const canEdit = useRef(ongoing);
  canEdit.current = ongoing;
  useEffect(() => {
    mounted.current = true;
    return () => { mounted.current = false; };
  }, []);

  const queueSave = (typeId, value) => {
    saves.current = saves.current.then(async () => {
      if (!mounted.current || !canEdit.current) return;
      try {
        await backendService.updateMeasurementValueInMetric(metric._id, typeId, value, ...(typeId === 'weight' ? ['lb'] : []));
        failedSaves.current.delete(typeId);
      } catch (error) {
        const status = error?.response?.status;
        failedSaves.current.set(typeId, { value, retryable: !status || status >= 500, message: error?.response?.data?.error || error?.response?.data?.message });
      }
      if (mounted.current) {
        const failures = [...failedSaves.current.values()];
        setSaveError(failures.length ? failures.map(f => f.message || (f.retryable ? 'Could not save measurements. Submit to retry.' : 'Measurements were rejected. Reload and correct the value and unit before submitting.')).join(' ') : '');
      }
    });
  };

  useEffect(() => {
    const fetchMeasurementTypes = async () => {
      try {
        const response = await backendService.fetchMeasurementTypes();
        setAvailableTypes(response);
      } catch (error) {
        console.error('Error fetching measurement types:', error);
      }
    };

    fetchMeasurementTypes();
  }, [backendService]);

  const handleAddMeasurement = (typeId, value, inputUnit = weightUnit) => {
    if (!mounted.current || !canEdit.current || completing.current) return;
    if (typeId === 'weight' && (!Number.isFinite(value) || value <= 0 || !['kg', 'lb', 'lbs'].includes(inputUnit))) {
      setSaveError('Enter a finite weight greater than zero in kg or lb.');
      return;
    }
    const valueToStore = typeId === 'weight' && inputUnit === 'kg' ? value / 0.45359237 : value;
    if (!Number.isFinite(valueToStore) || (typeId === 'weight' && valueToStore <= 0)) {
      setSaveError('Enter a finite weight greater than zero in kg or lb.');
      return;
    }
    saves.current = saves.current.then(async () => {
      if (!mounted.current || !canEdit.current) return;
      try {
        const updatedMetric = await backendService.addMeasurementToMetric(metric._id, typeId, valueToStore, ...(typeId === 'weight' ? ['lb'] : []));
        const added = updatedMetric.data.measurements.find(row => row.type_id === typeId);
        if (!added) throw new Error('Measurement addition was not persisted. Retry adding it before submitting.');
        failedAdds.current.delete(typeId);
        if (mounted.current && added) {
          updateMeasurements(previous => [...previous.filter(row => row.type_id !== typeId), added]);
        }
      } catch (error) {
        failedAdds.current.add(typeId);
        if (mounted.current) setSaveError(error?.response?.data?.error || error?.response?.data?.message || 'Could not add measurement. Check its value and unit, then try again.');
      }
    });
    return saves.current;
  };

  const handleRemoveMeasurement = (typeId) => {
    if (!mounted.current || !canEdit.current || completing.current) return;
    if (removing.current.has(typeId) || !window.confirm('Are you sure you want to remove this measurement?')) return;
    removing.current.add(typeId);
    setRemovingTypes(new Set(removing.current));
    saves.current = saves.current.then(async () => {
      try {
        await backendService.removeMeasurementFromMetric(metric._id, typeId);
        invalidDrafts.current.delete(typeId);
        latestValues.current.delete(typeId);
        failedSaves.current.delete(typeId);
        if (!mounted.current) return;
        updateMeasurements(previous => previous.filter(row => row.type_id !== typeId));
        setDrafts(previous => { const next = { ...previous }; delete next[typeId]; return next; });
      } catch (error) {
        if (mounted.current) alert('Failed to remove measurement.');
      } finally {
        removing.current.delete(typeId);
        if (mounted.current) setRemovingTypes(new Set(removing.current));
      }
    });
  };

  const handleValueChange = (typeId, newValue) => {
    if (skipBlurChange.current) { skipBlurChange.current = false; return; }
    if (!canEdit.current || completing.current || removing.current.has(typeId)) return;
    setDrafts(previous => ({ ...previous, [typeId]: { text: newValue ?? '', unit: weightUnit } }));
    if (newValue === null || String(newValue).trim() === '' || !Number.isFinite(Number(newValue)) || (typeId === 'weight' && (Number(newValue) <= 0 || !['kg', 'lb', 'lbs'].includes(weightUnit)))) {
      invalidDrafts.current.add(typeId);
      return;
    }
    invalidDrafts.current.delete(typeId);
    const value = Number(newValue);
    const valueToStore = typeId === 'weight' && weightUnit === 'kg' ? value / 0.45359237 : value;
    if (!Number.isFinite(valueToStore) || (typeId === 'weight' && valueToStore <= 0)) {
      invalidDrafts.current.add(typeId);
      return;
    }
    updateMeasurements(previous => previous.map(row => row.type_id === typeId ? { ...row, value: valueToStore, ...(typeId === 'weight' ? { unit: 'lb' } : {}) } : row));
    latestValues.current.set(typeId, valueToStore);
    queueSave(typeId, valueToStore);
  };

  const handleSubmit = async () => {
    if (submitting.current || !ongoing) return;
    const initiatingContext = getCompletionContext?.();
    submitting.current = true;
    setIsSubmitting(true);
    // Retry only settled failures, never append an older failed value after a new edit.
    const beforeRetry = saves.current;
    await beforeRetry;
    if (beforeRetry === saves.current) {
      for (const [typeId, failure] of failedSaves.current.entries()) {
        if (failure.retryable) queueSave(typeId, latestValues.current.get(typeId));
      }
    }
    let pending;
    do {
      pending = saves.current;
      await pending;
    } while (pending !== saves.current);
    try {
      if (mounted.current && canEdit.current) {
        if (measurementsRef.current.some(row => row.type_id === 'weight' && !validWeight(row))) setSaveError('Weight is missing a valid value or unit. Re-enter the weight with the displayed kg or lb unit before submitting.');
        else if (invalidDrafts.current.size) setSaveError('Enter a valid value for each measurement.');
        else if (failedAdds.current.size) setSaveError('A measurement addition failed. Retry adding it, or reload to discard the failed addition, before submitting.');
        else if (!failedSaves.current.size) {
          completing.current = true;
          setIsCompleting(true);
          if (initiatingContext) await finishActivity(metric._id, { initiatingContext });
          else await finishActivity(metric._id);
        }
      }
    } catch (error) {
      if (mounted.current) setSaveError(error?.response?.data?.error || error?.response?.data?.message || 'Could not submit measurements. Please try again.');
    } finally {
      completing.current = false;
      if (mounted.current) setIsCompleting(false);
      submitting.current = false;
      if (mounted.current) setIsSubmitting(false);
    }
  };

  // Draining local writes is part of saving; the parent then owns completion stages.
  const feedback = completion.pending || completion.saved || !isSubmitting || isCompleting
    ? completion : { pending: true, stage: 'saving', saved: false };

  const openAddMeasurementModal = () => {
    modalService.open(AddMeasurementModal, {
      availableTypes,
      onAdd: handleAddMeasurement,
    });
  };

  return (
    <div className="metrics">
      {saveError && <p role="alert">{saveError}</p>}
      {measurements.length > 0 && (
        <table className="activity-table">
          <thead>
            <tr>
              <th>Type</th>
              <th>Value</th>
              {ongoing && <th></th>}
            </tr>
          </thead>
          <tbody>
            {measurements.map((measurement) => {
              const archived = measurement.type_id === 'fat_percentage';
              const typeDetails = availableTypes.find((type) => type._id === measurement.type_id) || {};
              let displayValue = measurement.value;
              if (measurement.type_id === 'weight') {
                // Never interpret an ambiguous legacy value in the preferred unit.
                const pounds = measurement.unit === 'kg' ? measurement.value / 0.45359237 : measurement.value;
                displayValue = validWeight(measurement)
                  ? formatWeight(weightUnit === 'kg' ? pounds * 0.45359237 : pounds) : '';
              }

              return (
                <tr key={measurement.type_id}>
                  <td>{archived ? 'Body fat (archived manual measurement)' : typeDetails.name || 'Unknown Type'}</td>
                  <td data-unit-kind={measurement.type_id === 'weight' ? 'weight' : undefined}>
                    {ongoing && measurement.type_id === 'weight' && !validWeight(measurement) && <span>Re-enter weight ({weightUnit}); the previous unit/value is missing or invalid.</span>}
                    {archived ? <span>{displayValue} %</span> : ongoing ? (
                      <Input
                        type="number"
                        value={drafts[measurement.type_id]?.unit === weightUnit ? drafts[measurement.type_id].text : displayValue}
                        placeholder={measurement.type_id === 'weight' ? `Re-enter weight (${weightUnit})` : undefined}
                        onChange={(value) => handleValueChange(measurement.type_id, value)}
                        onBlur={(event) => {
                          // Input normally normalizes empty number fields to zero on blur.
                          // Keep this form's unfinished draft instead of persisting a made-up value.
                          skipBlurChange.current = event.target.value === '';
                        }}
                        disabled={isCompleting || metric.status === 'complete' || removingTypes.has(measurement.type_id)}
                      />
                    ) : (!isActivityExpired(metric) ? (
                      <span>{measurement.type_id === 'weight' && !validWeight(measurement) ? 'Weight unit/value missing — correction required' : `${displayValue} ${measurement.type_id === 'weight' ? weightUnit : ''}`}</span>
                    ) : (
                      <span>?</span>
                    ))}
                  </td>
                  {ongoing && !archived && (
                    <td>
                      <button
                        className="trash-button"
                        disabled={isCompleting || removingTypes.has(measurement.type_id)}
                        onClick={() => handleRemoveMeasurement(measurement.type_id)}
                      >
                        <TrashIcon />
                      </button>
                    </td>
                  )}
                </tr>
              );
            })}
          </tbody>
        </table>
      )}
      {ongoing && (
        <div className="center-button pad-button-up-big">
          <button className="btn-add" onClick={openAddMeasurementModal} disabled={isCompleting}>
            Add Measurement
          </button>
        </div>
      )}
      {!readOnly && (ongoing || completion.pending) && !metric.is_template && (
        <div className="center-button pad-button-up-small">
          <SubmitFeedbackButton
            className="btn-select"
            label="Submit Measurements"
            icon={<CheckIcon />}
            feedback={feedback}
            noun={SUBMIT_NOUNS.metric}
            onClick={handleSubmit}
            disabled={isSubmitting}
          />
        </div>
      )}
      <SubmitFeedbackAnnouncement feedback={feedback} noun={SUBMIT_NOUNS.metric} />
    </div>
  );
};

// A different activity owns a different draft and completion lifecycle.
const Metrics = props => <MetricsEditor key={props.metric._id} {...props} />;
export default Metrics;